import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, BookOpenCheck, CalendarClock, Flame, Loader2, Play, RefreshCw,
  Sparkles, Target, TrendingUp, Wrench, LifeBuoy, ScrollText,
} from 'lucide-react';
import { apiGet } from '@/lib/api';
import MasteryQuizDialog from '@/components/premium/MasteryQuizDialog';
import SessionRunnerDialog from '@/components/premium/SessionRunnerDialog';
import HomeworkHelpDialog from '@/components/premium/HomeworkHelpDialog';
import TonightNotesCard from '@/components/notes/TonightNotesCard';

/**
 * Phase 09 — the Daily Cockpit.
 *
 * ONE request fills this whole page (`GET /premium/v1/cockpit`), and the page is
 * written to read like a story: where you stand → what today asks of you → what
 * your brain wants back → what's shaky → practice → evidence → tomorrow.
 * A section that fails arrives as null and is quietly skipped; nothing here
 * shames a missed day and nothing predicts a paper.
 */

const Band = ({ children, delay = 0, testId }) => (
  <section data-testid={testId}
    className="opacity-0 translate-y-3 animate-[cockpitIn_.5s_ease-out_forwards]"
    style={{ animationDelay: `${delay}ms` }}>
    {children}
  </section>
);

// Tailwind needs literal classes, so accents live in a map rather than a template.
const ACCENTS = {
  indigo: ['border-indigo-100', 'text-indigo-500'],
  emerald: ['border-emerald-100', 'text-emerald-500'],
  rose: ['border-rose-100', 'text-rose-500'],
  sky: ['border-sky-100', 'text-sky-500'],
  amber: ['border-amber-100', 'text-amber-500'],
  violet: ['border-violet-100', 'text-violet-500'],
  slate: ['border-slate-200', 'text-slate-500'],
  orange: ['border-orange-100', 'text-orange-500'],
};

const Card = ({ title, icon: Icon, accent = 'indigo', children, action, testId }) => (
  <div className={`rounded-3xl border bg-white p-4 sm:p-5 ${ACCENTS[accent][0]} shadow-[0_1px_2px_rgba(15,23,42,.04)]`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
        <Icon className={`w-4 h-4 ${ACCENTS[accent][1]}`} /> {title}
      </p>
      {action}
    </div>
    <div className="mt-2.5">{children}</div>
  </div>
);

const Empty = ({ children }) => <p className="font-body text-[13px] text-slate-500">{children}</p>;

export default function CockpitPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [concept, setConcept] = useState(null);
  const [runnerSeed, setRunnerSeed] = useState(null);
  const [homeworkOpen, setHomeworkOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { setData(await apiGet('/premium/v1/cockpit')); setError(''); }
    catch (e) { setError(e?.message || 'The cockpit is unavailable right now'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="cockpit-error">
        <p className="font-heading text-base text-slate-800">We couldn't open your cockpit</p>
        <p className="font-body text-sm text-slate-500 mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm font-heading text-indigo-600 hover:underline" data-testid="cockpit-retry">
          Try again
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-400" data-testid="cockpit-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-body text-sm">Putting your day together…</span>
      </div>
    );
  }

  const exam = data.exam || {};
  const today = data.today || {};
  const tomorrow = data.tomorrow || {};

  const startSession = (item) => setRunnerSeed({
    subject: item?.subject || today.items?.[0]?.subject || '',
    chapter: (item?.label || '').replace(/^Revise: |^Full-portion sweep: /, '') || item?.chapter || '',
    concept_title: item?.title || '',
    concept_id: item?.concept_id,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="cockpit-page">
      <style>{`@keyframes cockpitIn{to{opacity:1;transform:none}}`}</style>

      {/* 1. Where you stand */}
      <Band delay={0} testId="cockpit-band-exam">
        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white p-5 sm:p-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <p className="text-[11px] font-heading uppercase tracking-[0.2em] text-white/70">Today's cockpit</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="cockpit-greeting">
            {data.student_name ? `${data.student_name.split(' ')[0]}, ` : ''}here's your day.
          </h1>
          {exam.has_exam ? (
            <p className="font-body text-sm text-white/90 mt-2" data-testid="cockpit-countdown">
              {exam.exam_name} · <strong>{exam.days_to_exam} day{exam.days_to_exam === 1 ? '' : 's'}</strong> to go ·
              portion {exam.portion_coverage_pct}% covered ({exam.items_done}/{exam.items_total})
            </p>
          ) : (
            <p className="font-body text-sm text-white/90 mt-2" data-testid="cockpit-no-exam">{exam.message}</p>
          )}
          <button onClick={() => navigate(exam.action?.route || exam.empty_action?.route || '/exams')}
            data-testid="cockpit-exam-action"
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-heading bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-full backdrop-blur-sm">
            {exam.action?.label || exam.empty_action?.label} <ArrowRight className="w-3 h-3" />
          </button>
          {exam.portion_at_risk && (
            <p className="font-body text-[12px] text-white mt-2 bg-rose-500/30 border border-white/20 rounded-xl px-3 py-1.5" data-testid="cockpit-at-risk">
              Some of the portion no longer fits at a safe pace — open the plan and we'll decide together what to trim.
            </p>
          )}
        </div>
      </Band>

      {/* 2. What today asks of you */}
      <Band delay={70} testId="cockpit-band-today">
        <Card title={`Today · ${today.minutes || 0} min`} icon={Target} accent="indigo" testId="cockpit-today"
          action={today.items?.length ? (
            <button onClick={() => startSession(today.items.find(i => !i.done) || today.items[0])}
              data-testid="cockpit-start-session"
              className="text-[11px] font-heading inline-flex items-center gap-1 bg-indigo-600 text-white px-2.5 py-1 rounded-full">
              <Play className="w-3 h-3" /> Start session
            </button>) : null}>
          {today.items?.length ? (
            <div className="space-y-2">
              {today.items.map(item => (
                <div key={item.ref} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`cockpit-today-item-${item.ref}`}>
                  <p className={`font-body text-[13px] ${item.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                    <span className="text-slate-400">{item.subject} · </span>{item.label}
                    <span className="text-slate-400"> · {item.minutes}m</span>
                  </p>
                  {(item.activities || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {item.activities.map(a => (
                        <button key={a.key} onClick={() => navigate(a.route)}
                          data-testid={`cockpit-chain-${item.ref}-${a.key}`}
                          className="text-[10px] font-body px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 transition-colors">
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : <Empty>{today.message}</Empty>}
        </Card>
      </Band>

      {/* 3. What your brain wants back */}
      <Band delay={140} testId="cockpit-band-revision">
        <Card title={`Revision due today${data.revision_due?.count ? ` · ${data.revision_due.count}` : ''}`}
          icon={RefreshCw} accent="emerald" testId="cockpit-revision">
          {data.revision_due?.due?.length ? (
            <div className="space-y-1.5">
              {data.revision_due.due.map(r => (
                <button key={r.concept_id} onClick={() => setConcept({ ...r, title: r.title })}
                  data-testid={`cockpit-revision-${r.concept_id}`}
                  className="w-full text-left rounded-2xl border border-emerald-100 bg-emerald-50/60 p-2.5 hover:border-emerald-300 transition-colors">
                  <p className="font-body text-[13px] text-slate-800">{r.title}</p>
                  <p className="font-body text-[11px] text-slate-500">
                    {r.subject} · mastery {r.mastery}% · due {r.due_on}
                    {r.exam_compressed ? ' · pulled closer for the exam' : ''}
                  </p>
                </button>
              ))}
            </div>
          ) : <Empty>{data.revision_due?.message}</Empty>}
        </Card>
      </Band>

      <Band delay={170} testId="cockpit-band-notes">
        <TonightNotesCard />
      </Band>

      {/* 4. What's shaky */}
      <Band delay={210} testId="cockpit-band-weak">
        <Card title="Worth fixing first" icon={Wrench} accent="rose" testId="cockpit-weak">
          {data.weak_concepts?.concepts?.length ? (
            <div className="space-y-1.5">
              {data.weak_concepts.concepts.map(c => (
                <div key={c.concept_id} className="rounded-2xl border border-rose-100 bg-rose-50/60 p-2.5 flex items-start justify-between gap-2"
                  data-testid={`cockpit-weak-${c.concept_id}`}>
                  <div className="min-w-0">
                    <p className="font-body text-[13px] text-slate-800 truncate">{c.title}</p>
                    <p className="font-body text-[11px] text-slate-500">{c.subject} · {c.reason}</p>
                  </div>
                  <button onClick={() => setConcept(c)} data-testid={`cockpit-fix-${c.concept_id}`}
                    className="text-[11px] font-heading bg-rose-600 text-white px-2.5 py-1 rounded-full flex-shrink-0 inline-flex items-center gap-1">
                    <LifeBuoy className="w-3 h-3" /> Fix this now
                  </button>
                </div>
              ))}
            </div>
          ) : <Empty>{data.weak_concepts?.message}</Empty>}
        </Card>
      </Band>

      {/* 5. Fresh practice */}
      <Band delay={280} testId="cockpit-band-practice">
        <Card title="Fresh practice" icon={BookOpenCheck} accent="sky" testId="cockpit-practice">
          {data.fresh_practice?.concepts?.length ? (
            <div className="grid sm:grid-cols-2 gap-2">
              {data.fresh_practice.concepts.map(c => (
                <button key={c.concept_id} onClick={() => setConcept(c)} data-testid={`cockpit-practice-${c.concept_id}`}
                  className="text-left rounded-2xl border border-sky-100 bg-sky-50/60 p-2.5 hover:border-sky-300 transition-colors">
                  <p className="font-body text-[13px] text-slate-800">{c.title}</p>
                  <p className="font-body text-[11px] text-slate-500">{c.subject} · mastery {c.mastery}% · {c.pool_size} questions</p>
                </button>
              ))}
            </div>
          ) : <Empty>{data.fresh_practice?.message}</Empty>}
        </Card>
      </Band>

      {/* 6. Evidence-based questions */}
      <Band delay={350} testId="cockpit-band-evidence">
        <Card title="High-value questions" icon={ScrollText} accent="amber" testId="cockpit-evidence"
          action={<button onClick={() => navigate('/question-bank')} data-testid="cockpit-open-question-bank"
            className="text-[11px] font-heading text-amber-700 hover:underline">Question Bank →</button>}>
          {data.evidence?.questions?.length ? (
            <div className="space-y-1.5">
              {data.evidence.questions.map(q => (
                <div key={q.evidence_id} className="rounded-2xl border border-amber-100 bg-amber-50/60 p-2.5" data-testid={`cockpit-evidence-${q.evidence_id}`}>
                  <p className="font-body text-[13px] text-slate-800">{q.question}</p>
                  <p className="font-body text-[11px] text-amber-800 mt-0.5">
                    {q.label} · appeared in {q.past_paper_count} past paper{q.past_paper_count === 1 ? '' : 's'}
                    {q.marks ? ` · ${q.marks} marks` : ''}{q.years?.length ? ` · ${q.years.join(', ')}` : ''}
                  </p>
                </div>
              ))}
              <p className="font-body text-[11px] text-slate-500 pt-1">{data.evidence.disclaimer}</p>
            </div>
          ) : <Empty>{data.evidence?.message}</Empty>}
        </Card>
      </Band>

      {/* 7. Homework + tomorrow + progress */}
      <Band delay={420} testId="cockpit-band-tail">
        <div className="grid sm:grid-cols-2 gap-3">
          <Card title="Homework help" icon={Sparkles} accent="violet" testId="cockpit-homework">
            <p className="font-body text-[13px] text-slate-600">
              Paste what you're stuck on. You'll get the method and a worked example that looks like it — never the answer you were set.
            </p>
            <button onClick={() => setHomeworkOpen(true)} data-testid="cockpit-homework-btn"
              className="mt-2 text-[11px] font-heading bg-violet-600 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1">
              Get unstuck <ArrowRight className="w-3 h-3" />
            </button>
          </Card>

          <Card title="Tomorrow" icon={CalendarClock} accent="slate" testId="cockpit-tomorrow">
            {tomorrow.items?.length ? (
              <ul className="space-y-1">
                {tomorrow.items.slice(0, 4).map(i => (
                  <li key={i.ref} className="font-body text-[13px] text-slate-700" data-testid={`cockpit-tomorrow-${i.ref}`}>
                    <span className="text-slate-400">{i.subject} · </span>{i.label}
                  </li>
                ))}
                <li className="font-body text-[11px] text-slate-400">{tomorrow.minutes} min planned</li>
              </ul>
            ) : <Empty>{tomorrow.message}</Empty>}
          </Card>
        </div>
      </Band>

      <Band delay={490} testId="cockpit-band-progress">
        <Card title="This week" icon={TrendingUp} accent="orange" testId="cockpit-progress"
          action={<button onClick={() => navigate('/growth')} data-testid="cockpit-open-growth"
            className="text-[11px] font-heading text-orange-700 hover:underline">Your growth →</button>}>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500" style={{ width: `${data.progress?.weekly_pct || 0}%` }} />
              </div>
              <p className="font-body text-[12px] text-slate-600 mt-1.5" data-testid="cockpit-progress-message">{data.progress?.message}</p>
            </div>
            {data.progress?.streak > 0 && (
              <span className="text-[12px] font-heading text-orange-600 flex items-center gap-1 flex-shrink-0" data-testid="cockpit-streak">
                <Flame className="w-3.5 h-3.5" /> {data.progress.streak}-day streak
              </span>
            )}
          </div>
        </Card>
      </Band>

      {data.degraded_sections?.length > 0 && (
        <p className="font-body text-[11px] text-slate-400" data-testid="cockpit-degraded">
          Some sections are taking a break right now ({data.degraded_sections.join(', ')}) — everything else is live.
        </p>
      )}

      {concept && (
        <MasteryQuizDialog open={!!concept} onOpenChange={(v) => { if (!v) { setConcept(null); load(); } }}
          concept={concept} />
      )}
      <SessionRunnerDialog open={!!runnerSeed} onOpenChange={(v) => { if (!v) { setRunnerSeed(null); load(); } }} seed={runnerSeed} />
      <HomeworkHelpDialog open={homeworkOpen} onOpenChange={setHomeworkOpen}
        subject={today.items?.[0]?.subject || ''} chapter={today.items?.[0]?.label || ''} />
    </div>
  );
}
